// This has to be a js file so gatsby-node.js can require it.
//
// Gatsby exposes env vars to browser bundles by replacing literal
// `process.env.GATSBY_X` member expressions at build time, so every var below
// must be read as a full literal expression at module top level — never
// `process.env[someName]`.

const LEGAL_PAGES = ["legal", "terms", "privacy", "marketing", "cookies"]

const legalRoutes = {
  legal: "/legal",
  terms: "/legal/terms",
  privacy: "/legal/privacy",
  marketing: "/legal/marketing",
  cookies: "/legal/cookies",
}

const VALUES = {
  GATSBY_LEGAL_PAGES: process.env.GATSBY_LEGAL_PAGES,
  GATSBY_COOKIEYES_URL: process.env.GATSBY_COOKIEYES_URL,
  GATSBY_LEGAL_LANGUAGE: process.env.GATSBY_LEGAL_LANGUAGE,
  GATSBY_LEGAL_PARTNER_SHORT_NAME: process.env.GATSBY_LEGAL_PARTNER_SHORT_NAME,
  GATSBY_LEGAL_PARTNER_COMPANY_NAME:
    process.env.GATSBY_LEGAL_PARTNER_COMPANY_NAME,
  GATSBY_LEGAL_PARTNER_VAT: process.env.GATSBY_LEGAL_PARTNER_VAT,
  GATSBY_LEGAL_PARTNER_SUPPORT_EMAIL:
    process.env.GATSBY_LEGAL_PARTNER_SUPPORT_EMAIL,
  GATSBY_LEGAL_PARTNER_DATA_PROCESSORS:
    process.env.GATSBY_LEGAL_PARTNER_DATA_PROCESSORS,
  GATSBY_LEGAL_PARTNER_PARTNERS: process.env.GATSBY_LEGAL_PARTNER_PARTNERS,
  GATSBY_LEGAL_CONTACT_INFO: process.env.GATSBY_LEGAL_CONTACT_INFO,
  GATSBY_LEGAL_TERMS_VERSION: process.env.GATSBY_LEGAL_TERMS_VERSION,
  GATSBY_LEGAL_TERMS_LAST_UPDATED: process.env.GATSBY_LEGAL_TERMS_LAST_UPDATED,
  GATSBY_PRIVACY_TERMS_VERSION: process.env.GATSBY_PRIVACY_TERMS_VERSION,
  GATSBY_PRIVACY_TERMS_LAST_UPDATED:
    process.env.GATSBY_PRIVACY_TERMS_LAST_UPDATED,
  GATSBY_MARKETING_TERMS_VERSION: process.env.GATSBY_MARKETING_TERMS_VERSION,
  GATSBY_MARKETING_TERMS_LAST_UPDATED:
    process.env.GATSBY_MARKETING_TERMS_LAST_UPDATED,
  GATSBY_TERMS: process.env.GATSBY_TERMS,
  GATSBY_PRIVACY: process.env.GATSBY_PRIVACY,
  GATSBY_COOKIES: process.env.GATSBY_COOKIES,
}

// The legacy all-or-nothing gate: with GATSBY_LEGAL_PAGES unset, every legal
// page is built iff all of these are present.
const LEGACY_GATE_VARS = [
  "GATSBY_LEGAL_LANGUAGE",
  "GATSBY_LEGAL_PARTNER_SHORT_NAME",
  "GATSBY_LEGAL_PARTNER_COMPANY_NAME",
  "GATSBY_LEGAL_PARTNER_VAT",
  "GATSBY_LEGAL_PARTNER_SUPPORT_EMAIL",
  "GATSBY_LEGAL_PARTNER_DATA_PROCESSORS",
  "GATSBY_LEGAL_PARTNER_PARTNERS",
  "GATSBY_LEGAL_CONTACT_INFO",
  "GATSBY_LEGAL_TERMS_VERSION",
  "GATSBY_LEGAL_TERMS_LAST_UPDATED",
  "GATSBY_PRIVACY_TERMS_VERSION",
  "GATSBY_PRIVACY_TERMS_LAST_UPDATED",
  "GATSBY_MARKETING_TERMS_VERSION",
  "GATSBY_MARKETING_TERMS_LAST_UPDATED",
]

// Vars a page's template actually renders — validated per page when the page
// is explicitly requested via GATSBY_LEGAL_PAGES.
const REQUIRED_VARS = {
  legal: ["GATSBY_LEGAL_LANGUAGE"],
  terms: [
    "GATSBY_LEGAL_LANGUAGE",
    "GATSBY_LEGAL_PARTNER_SHORT_NAME",
    "GATSBY_LEGAL_PARTNER_COMPANY_NAME",
    "GATSBY_LEGAL_PARTNER_VAT",
    "GATSBY_LEGAL_PARTNER_SUPPORT_EMAIL",
    "GATSBY_LEGAL_CONTACT_INFO",
    "GATSBY_LEGAL_TERMS_VERSION",
    "GATSBY_LEGAL_TERMS_LAST_UPDATED",
  ],
  privacy: [
    "GATSBY_LEGAL_LANGUAGE",
    "GATSBY_LEGAL_PARTNER_SHORT_NAME",
    "GATSBY_LEGAL_PARTNER_COMPANY_NAME",
    "GATSBY_LEGAL_PARTNER_VAT",
    "GATSBY_LEGAL_PARTNER_DATA_PROCESSORS",
    "GATSBY_LEGAL_CONTACT_INFO",
    "GATSBY_PRIVACY_TERMS_VERSION",
    "GATSBY_PRIVACY_TERMS_LAST_UPDATED",
  ],
  marketing: [
    "GATSBY_LEGAL_LANGUAGE",
    "GATSBY_LEGAL_PARTNER_SHORT_NAME",
    "GATSBY_LEGAL_PARTNER_PARTNERS",
    "GATSBY_LEGAL_CONTACT_INFO",
    "GATSBY_MARKETING_TERMS_VERSION",
    "GATSBY_MARKETING_TERMS_LAST_UPDATED",
  ],
  cookies: ["GATSBY_LEGAL_PARTNER_SHORT_NAME"],
}

// Pages that can fall back to an external URL when not built internally.
// There is no external-URL var for the /legal overview or /legal/marketing,
// so their links are hidden when the pages aren't built.
const EXTERNAL_URL_BY_PAGE = {
  terms: VALUES.GATSBY_TERMS,
  privacy: VALUES.GATSBY_PRIVACY,
  cookies: VALUES.GATSBY_COOKIES,
}

const parseTokens = () => {
  if (!VALUES.GATSBY_LEGAL_PAGES) {
    return []
  }
  return VALUES.GATSBY_LEGAL_PAGES.split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
}

const getConfiguredLegalPages = () => {
  const tokens = parseTokens()
  return tokens.length ? tokens : null
}

const getInvalidLegalPageTokens = () =>
  parseTokens().filter((token) => !LEGAL_PAGES.includes(token))

const getMissingVarsForPage = (page) =>
  REQUIRED_VARS[page].filter((name) => !VALUES[name])

const shouldBuildLegalPage = (page) => {
  if (page === "cookies" && VALUES.GATSBY_COOKIEYES_URL) {
    return false
  }
  const configuredPages = getConfiguredLegalPages()
  if (configuredPages) {
    return configuredPages.includes(page)
  }
  return LEGACY_GATE_VARS.every((name) => Boolean(VALUES[name]))
}

const resolveLegalLink = (page) => {
  if (page === "cookies" && VALUES.GATSBY_COOKIEYES_URL) {
    // CookieYes renders its own policy UI; never link our cookies page.
    return null
  }
  if (shouldBuildLegalPage(page)) {
    return { href: legalRoutes[page], internal: true }
  }
  const externalUrl = EXTERNAL_URL_BY_PAGE[page]
  return externalUrl ? { href: externalUrl, internal: false } : null
}

module.exports = {
  LEGAL_PAGES,
  legalRoutes,
  getConfiguredLegalPages,
  getInvalidLegalPageTokens,
  getMissingVarsForPage,
  shouldBuildLegalPage,
  resolveLegalLink,
}
